import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { NO_MEMBER_TAG_VALUE } from '@racemap/utilities/consts/starters';
import { Button } from 'antd';
import type { Immutable } from 'immer';
import React, { type FC, useMemo } from 'react';
import { IconExclamationTriangle } from '../../Icon';
import { type MultiValue, type OptionProps, Select, components } from '../Select';

export interface Option {
  value: string;
  label?: string;
  count?: number;
}
type Options = Immutable<Map<string, Array<Option>>>;

interface Props {
  options: Options;
  value: Options;
  onChange: (value: Options) => void;
  showOptions?: boolean;
  showCounts?: boolean;
  filterZeroCounts?: boolean;
  resetable?: boolean;
  showAbandoned?: boolean;
}

export const FilterSelector: FC<Props> = ({
  options,
  value,
  onChange,
  showOptions = false,
  showCounts = false,
  filterZeroCounts = false,
  resetable = true,
  showAbandoned = false,
}) => {
  const OptionComponent = useMemo(
    () =>
      function OptionComponent({ children, data, ...props }: OptionProps<Option, true>) {
        return (
          <components.Option data={data} {...props}>
            <OptionBody>
              {showCounts && <Count>{data.count || 0}</Count>} <Label>{children}</Label>
            </OptionBody>
          </components.Option>
        );
      },
    [showCounts],
  );

  const handleChange = (newTags: MultiValue<Option>, key: string) => {
    const newValue = new Map(value);

    if (newTags.length === 0) {
      newValue.delete(key);
    } else {
      newValue.set(key, newTags);
    }

    onChange(newValue);
  };

  const abondedTagGroups: Array<[string, Array<string>]> = [];
  for (const [tagKey, tagValue] of value) {
    if (tagKey === 'status') continue;
    if (!options.has(tagKey)) {
      abondedTagGroups.push([tagKey, tagValue.map((e) => e.value)]);
    }
  }

  return (
    <Container>
      {resetable && (
        <DeselectContainer>
          <DeselectButton
            disabled={value.size === 0}
            onClick={() => {
              onChange(new Map());
            }}
          >
            Reset Filter
          </DeselectButton>
        </DeselectContainer>
      )}
      <FilterContainer>
        {Array.from(options.entries(), ([tagKey, tagValue]) => {
          const selectedSubTags = getOptions(value.get(tagKey));
          const options = getOptions(tagValue, filterZeroCounts);
          const placeholder = options.map((e) => e.label || e.value).join(' | ');

          return (
            <React.Fragment key={tagKey}>
              <FilterTitle key={`filter-title-${tagKey}`}>{tagKey}:</FilterTitle>
              <FilterSelectContainer key={`filter-select-${tagKey}`}>
                <Select<Option, true>
                  isMulti
                  closeMenuOnSelect={false}
                  name={`select-${tagKey}`}
                  options={options}
                  value={selectedSubTags}
                  onChange={(value) => handleChange(value, tagKey)}
                  placeholder={showOptions ? placeholder : 'Select...'}
                  components={{ Option: OptionComponent }}
                />
              </FilterSelectContainer>
            </React.Fragment>
          );
        })}
        {showAbandoned &&
          abondedTagGroups.map(([tagKey]) => {
            return (
              <React.Fragment key={tagKey}>
                <FilterTitle key={`filter-title-${tagKey}`}>{tagKey}:</FilterTitle>
                <FilterSelectContainer key={`filter-select-${tagKey}`}>
                  <Button
                    type="link"
                    icon={<IconExclamationTriangle />}
                    onClick={() => handleChange([], tagKey)}
                  >
                    Abonded, delete?
                  </Button>
                </FilterSelectContainer>
              </React.Fragment>
            );
          })}
      </FilterContainer>
    </Container>
  );
};

function getOptions(values?: Immutable<Array<Option>>, filterZeroCounts = false): Array<Option> {
  if (values == null) return [];
  // if label is undefined, use value instead
  return (
    values
      .map((e) => ({ label: e.value === NO_MEMBER_TAG_VALUE ? '(none)' : e.value, ...e }))
      // filter values with a defined count greater zero
      .filter((e) => !filterZeroCounts || e.count == null || e.count !== 0)
      .sort((a, b) => {
        if (a.value === NO_MEMBER_TAG_VALUE) return 1;
        if (b.value === NO_MEMBER_TAG_VALUE) return -1;
        return a.label.localeCompare(b.label);
      })
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const DeselectContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
`;

const DeselectButton = styled(Button)`
  width: 75%;
  height: 22px;
  padding: unset;
  font-size: smaller;
`;

const FilterContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr minmax(0, 4fr);
  row-gap: 15px;
  column-gap: 20px;

  .abonded-tag-groups {
    width: 100%;
    
  }
`;

const FilterTitle = styled.div`
  grid-column: 1;
  align-self: center;
  font-weight: 600;
  text-transform: capitalize;
`;

const FilterSelectContainer = styled.div`
  grid-column: 2;
  justify-self: end;
  overflow: hidden;
  width: 100%;
`;

const OptionBody = styled.div`
  display: flex;
  flex-direction: row;
`;

const Label = styled.div`
  font-weight: 450;
`;

const Count = styled.div`
  padding-right: 8px;
  color: ${RacemapColors.DarkGray};
  text-align: end;
  width: 5ch;
`;
